"use client";

import { lazy, Suspense } from "react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import { PageContainer } from "@/components/ui/PageContainer";
import HeroDemo from "@/components/HeroDemo";

const GrowthOrbit = lazy(() => import("@/components/GrowthOrbit"));

const HERO_SRC = "/media/images/hero/hero-bg.webp";
const HERO_SRCSET =
  "/media/images/hero/hero-bg-640.webp 640w, /media/images/hero/hero-bg-960.webp 960w, /media/images/hero/hero-bg.webp 1280w";

function HeroBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
      <div className="absolute inset-0">
        <Image
          src={HERO_SRC}
          srcSet={HERO_SRCSET}
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-right-top opacity-90"
        />
      </div>

      {/* Legibility scrims: keep left/center dark for headline, fade section into the base dark */}
      <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(5,5,5,0.96)_0%,rgba(5,5,5,0.78)_38%,rgba(5,5,5,0.35)_68%,rgba(5,5,5,0.15)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[linear-gradient(to_top,#050505_0%,rgba(5,5,5,0.85)_28%,transparent_100%)]" />
    </div>
  );
}

export default function Hero() {
  return (
    <section className="page-hero relative overflow-x-hidden pb-16 md:pb-24">
      <HeroBackground />
      <PageContainer>
        <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-8">
          <div className="relative z-10 mx-auto max-w-3xl text-center lg:mx-0 lg:text-left">
            <p className="font-ui text-sm font-semibold tracking-wide text-accent">
              Global growth partner · US · UK · Australia · UAE
            </p>
            <h1 className="font-display mt-4 text-h1 text-white">
              <span className="block">You focus on growth.</span>
              <span className="block text-gradient-accent">We handle the rest.</span>
            </h1>
            <p className="font-ui mx-auto mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg lg:mx-0">
              Strategy, brand, product, and marketing under one roof—for teams in the US, UK, AU, and UAE.
            </p>
            <div className="hero-cta-enter mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:justify-start">
              <Button
                href="/blueprint?source=hero-cta"
                utmPlacement="hero-cta"
                className="w-full sm:w-auto"
              >
                Get your free blueprint
              </Button>
              <Button
                href="/book"
                variant="secondary"
                utmPlacement="hero-book"
                className="w-full sm:w-auto"
              >
                Book a strategy call
              </Button>
            </div>
          </div>

          <div className="relative z-10 mx-auto w-full max-w-md lg:max-w-none">
            <Suspense fallback={<div className="aspect-square w-full" aria-hidden />}>
              <GrowthOrbit />
            </Suspense>
          </div>
        </div>

        <div className="relative z-10 mt-16 lg:mt-20">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-white sm:text-2xl">
                From first call to launch
              </h2>
              <p className="font-ui mt-1 text-sm text-zinc-400">
                One team owns the brief through ship.
              </p>
            </div>
            <Link
              href="/blueprint?source=hero-demo"
              className="font-ui text-sm font-semibold text-accent transition-colors hover:text-accent-hover"
              data-utm-placement="hero-add-blueprint"
            >
              Try the Blueprint tool →
            </Link>
          </div>
          <HeroDemo />
        </div>
      </PageContainer>
    </section>
  );
}
